import { useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface AuthGuardProps {
  requireAuth?: boolean;
  requireVerification?: boolean;
}

export function AuthGuard({ 
  requireAuth = true, 
  requireVerification = true 
}: AuthGuardProps) {
  const { isAuthenticated, isEmailVerified, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Refresh the user session whenever AuthGuard is mounted or location changes
  useEffect(() => {
    const checkSession = async () => {
      // Only check if the user thinks they're authenticated
      if (isAuthenticated) {
        try {
          const { data, error } = await supabase.auth.getSession();
          
          // If no session or error, sign out
          if (error || !data.session) {
            await signOut();
            navigate('/auth/login');
          }
        } catch (error) {
          console.error('Error checking session:', error);
          await signOut();
          navigate('/auth/login');
        }
      }
    };
    
    checkSession();
  }, [isAuthenticated, navigate, signOut, location.pathname]);

  useEffect(() => {
    if (!loading) {
      // Special case: unverified users should be in verify-email or login only
      const isVerifyEmailPath = location.pathname === '/auth/verify-email';
      
      if (requireAuth && isAuthenticated && !isEmailVerified) {
        // User is authenticated but not verified - should only access allowed unverified paths
        // or should be logged out
        if (!isVerifyEmailPath && location.pathname !== '/auth/login') {
          navigate('/auth/verify-email');
          return;
        }
      }

      // For protected routes that require verification
      if (requireAuth && requireVerification) {
        if (!isAuthenticated) {
          navigate('/auth/login');
          return;
        }
        
        if (isAuthenticated && !isEmailVerified && !isVerifyEmailPath) {
          navigate('/auth/verify-email');
          return;
        }
      }
      
      // For protected routes that don't require verification (like verification page)
      if (requireAuth && !requireVerification) {
        if (!isAuthenticated) {
          navigate('/auth/login');
          return;
        }
      }
      
      // For auth-only routes (login, register) - redirect to dashboard if already authenticated and verified
      if (!requireAuth && isAuthenticated) {
        if (isEmailVerified) {
          navigate('/');
          return;
        } else if (!isVerifyEmailPath) {
          // If authenticated but not verified, redirect to verify-email
          navigate('/auth/verify-email');
          return;
        }
      }
    }
  }, [isAuthenticated, isEmailVerified, loading, navigate, requireAuth, requireVerification, location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Conditions to render the outlet:
  // 1. If route requires auth AND user is authenticated AND
  //    (verification not required OR user is verified)
  // 2. If route doesn't require auth AND user is not authenticated
  if (
    (requireAuth && isAuthenticated && (!requireVerification || isEmailVerified)) ||
    (requireAuth && isAuthenticated && !requireVerification) ||
    (!requireAuth && !isAuthenticated)
  ) {
    return <Outlet />;
  }

  return null;
}